import { Droplets, Wind, Flame, Mountain, CircleDashed } from "lucide-react";
import { IMG } from "../lib/assets";

const principles = [
  {
    n: "01",
    title: "Directions Govern Destiny",
    body: "Each of the eight primary directions is ruled by a planet and governs a distinct life area — wealth, relationships, reputation, clarity. Correct a direction, correct a life area.",
  },
  {
    n: "02",
    title: "Five Elements in Balance",
    body: "Water, Fire, Earth, Air and Space must exist in exact zones in exact proportions. An imbalanced element silently tilts outcomes for years.",
  },
  {
    n: "03",
    title: "Sixteen Zones, Sixteen Lives",
    body: "Every built space is a 16-zone grid. Each zone is a lever that — when activated correctly — uplifts a specific chapter of your life.",
  },
  {
    n: "04",
    title: "No Demolition, Only Devotion",
    body: "Vastu remedies are subtle — colour, metal, salt, mantra, placement. Real shifts are created without breaking a single wall.",
  },
];

const zones = [
  { dir: "N", name: "Career & Customers", planet: "Mercury", element: "Water" },
  { dir: "NE", name: "Clarity & Motivation", planet: "Jupiter", element: "Water" },
  { dir: "E", name: "Networking & Social Connection", planet: "Sun", element: "Air" },
  { dir: "SE", name: "Cashflow & Money", planet: "Venus", element: "Fire" },
  { dir: "S", name: "Fame & Recognition", planet: "Mars", element: "Fire" },
  { dir: "SW", name: "Relationship & Skills", planet: "Rahu", element: "Earth" },
  { dir: "W", name: "Gains & Profits", planet: "Saturn", element: "Space" },
  { dir: "NW", name: "Support", planet: "Moon", element: "Space" },
  { dir: "NNE", name: "Health & Immunity", planet: "—", element: "Water" },
  { dir: "ENE", name: "Happiness", planet: "—", element: "Air" },
  { dir: "ESE", name: "Overthinking", planet: "—", element: "Air-Fire" },
  { dir: "SSE", name: "Power & Confidence", planet: "—", element: "Fire" },
  { dir: "SSW", name: "Expenditure & Disposal", planet: "—", element: "Earth" },
  { dir: "WSW", name: "Education & Savings", planet: "—", element: "Space" },
  { dir: "WNW", name: "Depression & Detoxify", planet: "—", element: "Space" },
  { dir: "NNW", name: "Sexuality & Attraction", planet: "—", element: "Water" },
];

const elements = [
  { icon: Mountain, name: "Prithvi · Earth", attrs: "Stability · Structure · Grounding", effect: "The steady base of every life and every home." },
  { icon: Droplets, name: "Jal · Water", attrs: "Flow · Emotions · Healing", effect: "The current that carries clarity and renewal." },
  { icon: Flame, name: "Agni · Fire", attrs: "Energy · Transformation · Passion", effect: "The spark that turns intention into action." },
  { icon: Wind, name: "Vayu · Air", attrs: "Movement · Communication · Joy", effect: "The breath that circulates life between people." },
  { icon: CircleDashed, name: "Akash · Space", attrs: "Expansion · Cosmic Energy · Connection", effect: "The canvas on which all four elements dance." },
];

export default function Vastu() {
  return (
    <div data-testid="vastu-page" className="pt-32">
      {/* HEADER */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <img src={IMG.heroMandala} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FBF7F0]/80 via-[#FBF7F0]/90 to-[#FBF7F0]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-6">The Tarini Syllabus</div>
          <h1 className="font-bodoni text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02] max-w-4xl">
            The Vastu <span className="italic text-[#A67C00]">principles</span>,
            laid out plainly.
          </h1>
          <p className="mt-8 text-[#6B5F55] max-w-2xl text-lg">
            A field guide to the four laws, sixteen zones, and five elements that silently
            govern your home, workplace and well-being.
          </p>
        </div>
      </section>

      {/* FOUR PRINCIPLES */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-4">I. Four Core Principles</div>
          <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-16 max-w-3xl">
            The foundation every consultation stands on.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((p, i) => (
              <div
                key={p.n}
                data-testid={`principle-${i}`}
                className={`yantra-card p-10 bg-white ${i % 2 === 1 ? "md:translate-y-12" : ""}`}
              >
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-bodoni text-5xl text-[#A67C00] leading-none">{p.n}</span>
                  <div className="yantra-divider flex-1" />
                </div>
                <div className="font-bodoni text-2xl text-[#1E1A17] mb-3">{p.title}</div>
                <p className="text-[#6B5F55] leading-relaxed text-sm">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 16 ZONES GRID */}
      <section className="py-24 md:py-32 bg-[#F3EADB] border-y border-[#C9A227]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-4">II. The 16 Zones</div>
          <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6 max-w-3xl">
            Each zone is a lever. Together, they write the script of your life.
          </h2>
          <p className="text-[#6B5F55] max-w-2xl mb-16">
            The eight primary directions and their eight sub-directions. Every wall, appliance and
            entrance belongs to one — and must serve its natural function.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#C9A227]/15">
            {zones.map((z, i) => (
              <div
                key={z.dir}
                data-testid={`zone-${i}`}
                className="bg-[#F3EADB] hover:bg-white transition-colors p-6 min-h-[160px] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between">
                  <span className="font-bodoni text-3xl text-[#A67C00]">{z.dir}</span>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-[#A67C00]/60">
                    {z.element}
                  </span>
                </div>
                <div>
                  <div className="font-bodoni text-lg text-[#1E1A17]">{z.name}</div>
                  {z.planet !== "—" && (
                    <div className="text-xs text-[#6B5F55] mt-1">Ruled by {z.planet}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5 ELEMENTS */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-4">III. The Five Elements</div>
          <h2 className="font-bodoni text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-16 max-w-3xl">
            Panchamahabhuta — the five that compose <span className="italic text-[#A67C00]">everything</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {elements.map((e, i) => (
              <div
                key={e.name}
                data-testid={`element-${i}`}
                className="yantra-card p-8 bg-white flex flex-col items-start min-h-[240px]"
                style={{ transform: `translateY(${i % 2 === 0 ? "0" : "24px"})` }}
              >
                <e.icon className="text-[#A67C00]" size={28} />
                <div className="font-bodoni text-xl text-[#1E1A17] mt-6">{e.name}</div>
                <div className="overline mt-2">{e.attrs}</div>
                <p className="text-xs text-[#6B5F55] mt-4 leading-relaxed">{e.effect}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
